import React, { useEffect } from 'react';
import styled from '@emotion/styled';
import { useHistory, useParams } from 'react-router';
import { useFetchTopicWithRsvpsQuery } from '@modernhealth/circles-graphql/v2';
import { layout, colors } from '@modernhealth/vital-foundation';
import { useIntl } from 'react-intl';
import {
    BodyTextMedium,
    HeadingText50,
    Button,
    HeadingText40,
    ComponentTextSmall,
    renderMessage,
    CalloutInline,
} from '@modernhealth/vital-components';
import moment, { Moment } from 'moment';
import { CircleHostCard, SeriesAccessType } from '@modernhealth/circles-web-components';
import { filterNullish } from '@modernhealth/filter-nullish';
import { useTranslation } from 'react-i18next';

import { captureException } from 'src/shared/helpers/captureException';
import { SharedPreferences } from 'src/shared/models/shared';
import { useSharedPreferences } from 'src/shared/helpers/useSharedPreferences';
import { InternalLink } from 'src/shared/components/InternalLink';
import { Grid } from 'src/shared/components/Grid';

import { BackButton } from '../components/BackButton';
import TopicPageSkeleton from '../components/TopicPageSkeleton';
import { CircleActiveRsvpList } from '../components/CircleActiveRsvpList';
import { CircleSessionItemList } from '../components/CircleSessionItemList';
import { TranslatedSeriesAccess } from '../components/TranslatedSeriesAccess';
import { TranslatedCircleFormat } from '../components/TranslatedCircleFormat';
import { isSeriesMembersOnly } from '../helpers/isSeriesMembersOnly';
import { TranslatedDuration } from '../components/TranslatedDuration';
import { getErrorMessage } from '../utils/getErrorMessage';
import { InlineBulletList } from '../components/InlineBulletList';

interface TopicProps {
    topicSlug: string;
}

export const dateAddMinutes = (date: string, minutes: number): Moment => {
    return moment(date).clone().add(minutes, 'minutes');
};

const NoteText = styled(BodyTextMedium)({
    color: colors.gray85,
    fontWeight: 600,
    marginBottom: 48,
});

const Wrapper = styled.div({
    width: '100%',
    maxWidth: '560px',
    margin: '0 auto',
    paddingLeft: layout.containerPaddingX,
    paddingRight: layout.containerPaddingX,
    paddingBottom: layout.footerHeight,
});

const TopicInfoWrapper = styled.div({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 30,
});

export const CirclesTopicPage = () => {
    const { topicSlug } = useParams<TopicProps>();
    const history = useHistory();
    const { t } = useTranslation('circles');
    const intl = useIntl();

    const { data, loading, error, refetch } = useFetchTopicWithRsvpsQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            topicSlug,
        },
    });

    const firstTimeToRsvp = !useSharedPreferences(SharedPreferences.CirclesAgreedToGuidelines);
    useEffect(() => {
        if (error) {
            captureException(error);
            renderMessage({
                content: t(getErrorMessage()),
                type: 'error',
                duration: 5,
            });
        }
    }, [error, history, t]);

    if (loading && !data) {
        return (
            <Wrapper>
                <TopicPageSkeleton />;
            </Wrapper>
        );
    }

    if (!data?.circleTopicBySlug) {
        return (
            <div>
                Failed to load upcoming circles.
                <Button onClick={() => refetch()}>Retry</Button>
            </div>
        );
    }

    const topic = data.circleTopicBySlug;
    const {
        name,
        description,
        note,
        sessions,
        duration,
        rsvpQuestions,
        format,
        activeRsvps,
        facilitators,
    } = topic;

    const sessionCount = sessions.edges.length;
    if (sessionCount && sessionCount >= 100) {
        captureException(`Circle sessions were truncated at 100!`, { count: sessionCount });
    }

    const useRsvpPage = (rsvpQuestions && rsvpQuestions.length > 0) || firstTimeToRsvp;
    const hosts = filterNullish(facilitators.edges);
    const isMemberOnly = isSeriesMembersOnly(topic.series);

    return (
        <Wrapper>
            <div css={{ marginBottom: '20px' }}>
                <BackButton fallbackPath="/circles">
                    {previousRoute => {
                        if (previousRoute === '/circles/upcoming') {
                            return t('routes.upcoming.title');
                        } else if (previousRoute === `/circles`) {
                            return t('routes.fallback.title');
                        } else if (previousRoute?.startsWith('/circles/series')) {
                            return t('routes.series.title');
                        }

                        return null;
                    }}
                </BackButton>
            </div>
            {isMemberOnly && (
                <ComponentTextSmall
                    marginBottom={7}
                    fontColor="violet55"
                    css={{
                        fontWeight: 500,
                        textTransform: 'uppercase',
                    }}
                >
                    <TranslatedSeriesAccess
                        accessType={SeriesAccessType.MODERN_HEALTH_MEMBERS_ONLY}
                    />
                </ComponentTextSmall>
            )}
            <HeadingText50 marginBottom={14}>{name}</HeadingText50>
            <TopicInfoWrapper>
                <BodyTextMedium>
                    <InlineBulletList>
                        <TranslatedCircleFormat format={format} />
                        <TranslatedDuration minutes={duration} />
                    </InlineBulletList>
                </BodyTextMedium>
            </TopicInfoWrapper>
            <BodyTextMedium fontColor="gray65" css={{ marginBottom: 20 }}>
                {description}
            </BodyTextMedium>
            <NoteText>{note}</NoteText>
            {hosts.length > 0 && (
                <section css={{ marginBottom: 20 }}>
                    <HeadingText40 marginBottom={26}>
                        {t('pages.topic.providers_section.title')}
                    </HeadingText40>
                    <Grid minItemWidth={200} columnGap={16} rowGap={16} expand>
                        {hosts.map(({ node: host }) => (
                            <CircleHostCard
                                key={host.id}
                                photo={host.photo}
                                credential={host.credential}
                                name={host.name}
                                country={
                                    host.countryCode
                                        ? intl.formatDisplayName(host.countryCode, {
                                              type: 'region',
                                          })
                                        : host.country
                                }
                            />
                        ))}
                    </Grid>
                </section>
            )}
            {activeRsvps && activeRsvps.length > 0 && (
                <CircleActiveRsvpList
                    activeRsvps={activeRsvps}
                    duration={topic.duration}
                    topicSlug={topicSlug}
                />
            )}
            {sessions.edges.length > 0 && (
                <CircleSessionItemList
                    topic={topic}
                    topicSlug={topicSlug}
                    useRsvpPage={useRsvpPage}
                />
            )}
            {sessions.edges.length === 0 &&
                (!activeRsvps || (activeRsvps && activeRsvps.length === 0)) && (
                    <CalloutInline variant="info">
                        {t('circles:pages.topic.no_upcoming_sessions')}
                        <InternalLink to="/circles/upcoming" css={{ textTransform: 'lowercase' }}>
                            {t('circles:pages.upcoming.header')}
                        </InternalLink>
                    </CalloutInline>
                )}
        </Wrapper>
    );
};
